import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './EditorPDF.css';
import { getBordas, upsertBordas } from '../../services/api';
import { rotacionarLado, rotacionarLadoAprendido } from '../../utils/furoTransforms';

/**
 * EditorPDF - Editor visual para ajustar PDFs antes de gerar
 * Com cotas arrastáveis e configuração de bordas
 */
function EditorPDF({ pecas, onClose, onGenerate }) {
  const [pecaAtualIndex, setPecaAtualIndex] = useState(0);
  const [configPecas, setConfigPecas] = useState({});
  const [cotaSelecionada, setCotaSelecionada] = useState(null);
  const [arrastando, setArrastando] = useState(false);
  const [cotasElementos, setCotasElementos] = useState([]);
  const [tooltip, setTooltip] = useState(null);
  const [hoveredFuro, setHoveredFuro] = useState(null);
  const canvasRef = useRef(null);
  const escalaRef = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });
  const rasgosRef = useRef([]);
  const furosRef = useRef([]);

  // ========================================
  // TRANSFORMAÇÃO (rotação/espelhamento)
  // ========================================
  const [transformacoes, setTransformacoes] = useState({});
  const [pecasOriginais, setPecasOriginais] = useState({});

  
  const pecaAtual = pecas[pecaAtualIndex];
  const configAtual = useMemo(() => configPecas[pecaAtual?.id] || {
    bordas: { topo: 'nenhum', baixo: 'nenhum', esquerda: 'nenhum', direita: 'nenhum' },
    cotasOffset: {}
  }, [configPecas, pecaAtual?.id]);
  const transformacaoAtual = transformacoes[pecaAtual?.id] || { rotacao: 0, espelhado: false, invertido: false };


  // Calcular mandril baseado na posição Y e batente
  const calcularMandril = (yFuro, batente) => {
    const distancia = Math.abs(yFuro - batente);
    if (distancia <= 50) return 1;
    if (distancia <= 100) return 2;
    if (distancia <= 150) return 3;
    if (distancia <= 200) return 4;
    if (distancia <= 250) return 5;
    return 6;
  };

  // Calcular batente (menor Y dos furos ou 50)
  const calcularBatente = (peca) => {
    const todosY = [];
    (peca.furos?.verticais || []).forEach(f => todosY.push(f.y));
    (peca.furos?.horizontais || []).forEach(f => todosY.push(f.y));
    if (todosY.length === 0) return 50;
    return Math.min(...todosY);
  };

  // Inicializar configurações das peças
  useEffect(() => {
    const configInicial = {};
    const originais = {};
    const transformacoesIniciais = {};
    pecas.forEach(peca => {
      // Normalizar bordas: garante que todas as chaves existam como strings válidas
      // (peca.bordas pode vir do banco como null, {} ou com chaves faltando)
      const b = peca.bordas || {};
      configInicial[peca.id] = {
        bordas: {
          topo:     b.topo     || 'nenhum',
          baixo:    b.baixo    || 'nenhum',
          esquerda: b.esquerda || 'nenhum',
          direita:  b.direita  || 'nenhum',
        },
        cotasOffset: {}
      };
      // Salvar estado original para transformações
      originais[peca.id] = {
        comprimento: parseFloat(peca.comprimento) || 500,
        largura: parseFloat(peca.largura) || 300,
        furosVerticais: peca.furos?.verticais || [],
        furosHorizontais: peca.furos?.horizontais || []
      };
      // Carregar transformação salva para que o EditorPDF já mostre a peça na orientação correta
      if (peca.transformacao) {
        transformacoesIniciais[peca.id] = {
          rotacao: peca.transformacao.rotacao || 0,
          espelhado: peca.transformacao.espelhado || false,
          invertido: peca.transformacao.invertido || false,
        };
      }
    });
    setConfigPecas(configInicial);
    setPecasOriginais(originais);
    setTransformacoes(transformacoesIniciais);

    // Carrega bordas da tabela bordas_peca e sobrescreve o estado local.
    // Fire-and-forget: se falhar (peça sem bordas ou offline), mantém o fallback do JSON column.
    const _tr = (b) =>
      b.estado === 'fitada' && b.tipo_fita === 'cor'   ? 'cor'  :
      b.estado === 'fitada' && b.tipo_fita === 'pardo' ? 'pardo':
                                                          'nenhum';
    pecas.forEach(peca => {
      getBordas(peca.id).then(data => {
        setConfigPecas(prev => ({
          ...prev,
          [peca.id]: {
            ...prev[peca.id],
            bordas: {
              topo:     _tr(data.topo),
              baixo:    _tr(data.baixo),
              esquerda: _tr(data.esquerda),
              direita:  _tr(data.direita),
            },
            bordasPersistido: {
              topo:     data.topo.persistido,
              baixo:    data.baixo.persistido,
              esquerda: data.esquerda.persistido,
              direita:  data.direita.persistido,
            },
          },
        }));
      }).catch(err => console.error('bordas load:', err));
    });
  }, [pecas]);

  const aplicarTransformacao = (novaTransformacao) => {
    if (!pecaAtual) return;
    const original = pecasOriginais[pecaAtual.id];
    if (!original) {
      setTransformacoes(prev => ({ ...prev, [pecaAtual.id]: novaTransformacao }));
      return;
    }

    // NÃO mutar pecaAtual — apenas salvar a transformação
    // O canvas usa pecasOriginais + transformação pra desenhar
    setTransformacoes(prev => ({ ...prev, [pecaAtual.id]: novaTransformacao }));
    
    // Resetar cotas ao transformar
    setConfigPecas(prev => ({
      ...prev,
      [pecaAtual.id]: { ...prev[pecaAtual.id], cotasOffset: {} }
    }));
    setCotaSelecionada(null);
  };

  const desenharPreview = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !pecaAtual) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    ctx.clearRect(0, 0, width, height);

    // ✅ Calcular dados transformados a partir do ORIGINAL
    const original = pecasOriginais[pecaAtual.id];
    const transf = transformacoes[pecaAtual.id] || { rotacao: 0, espelhado: false, invertido: false };
    const LADO_INV = { 'LS': 'LSU', 'LSL': 'LSU', 'LSU': 'LS' };

    let comprimento, largura, furosVerticais, furosHorizontais;

    if (original && (transf.rotacao !== 0 || transf.espelhado || transf.invertido)) {
      const rotacao = transf.rotacao;
      const espelhado = transf.espelhado;
      const invertido = transf.invertido;

      comprimento = (rotacao === 90 || rotacao === 270) ? original.largura : original.comprimento;
      largura = (rotacao === 90 || rotacao === 270) ? original.comprimento : original.largura;

      // Transformar furos verticais
      furosVerticais = (original.furosVerticais || []).map(f => {
        let x = f.x, y = f.y, lado = f.lado;
        if (rotacao === 90) { const nx = original.largura - y; const ny = x; x = nx; y = ny; }
        else if (rotacao === 180) { x = original.comprimento - x; y = original.largura - y; }
        else if (rotacao === 270) { const nx = y; const ny = original.comprimento - x; x = nx; y = ny; }
        if (espelhado) { y = largura - y; }
        if (invertido) { lado = LADO_INV[lado] || lado; }
        return { ...f, x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10, lado };
      });

      // Transformar furos horizontais
      furosHorizontais = (original.furosHorizontais || []).map(f => {
        let x = f.x, y = f.y, lado = f.lado;
        if (rotacao === 90) { const nx = original.largura - y; const ny = x; x = nx; y = ny; lado = rotacionarLado(lado, 90); }
        else if (rotacao === 180) {
          x = original.comprimento - x; y = original.largura - y;
          // Validado contra a regra oficial [CNC] (ciclo 9) — ver
          // docs/referencia/formato-mpr/campo-bm-horizontal.md §9.
          lado = rotacionarLadoAprendido(f, 180, original.comprimento, original.largura);
        }
        else if (rotacao === 270) { const nx = y; const ny = original.comprimento - x; x = nx; y = ny; lado = rotacionarLado(lado, 270); }
        if (espelhado) { y = largura - y; if (lado === 'YM') lado = 'YP'; else if (lado === 'YP') lado = 'YM'; }
        return { ...f, x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10, lado };
      });
    } else {
      comprimento = parseFloat(pecaAtual.comprimento) || 500;
      largura = parseFloat(pecaAtual.largura) || 300;
      furosVerticais = pecaAtual.furos?.verticais || [];
      furosHorizontais = pecaAtual.furos?.horizontais || [];
    }
    
    const espessura = parseFloat(pecaAtual.espessura) || 15;
    const batente = calcularBatente({ furos: { verticais: furosVerticais, horizontais: furosHorizontais } });

    // Calcular escala para caber no canvas
    const margemCanvas = 100;
    const escalaX = (width - margemCanvas * 2) / comprimento;
    const escalaY = (height - margemCanvas * 2) / largura;
    const escala = Math.min(escalaX, escalaY, 0.8);
    escalaRef.current = escala;

    const larguraDesenho = comprimento * escala;
    const alturaDesenho = largura * escala;

    // Centralizar
    const offsetX = (width - larguraDesenho) / 2;
    const offsetY = (height - alturaDesenho) / 2;
    offsetRef.current = { x: offsetX, y: offsetY };

    // Desenhar fundo
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, width, height);

    // Grid de fundo
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 0.5;
    for (let i = 0; i < width; i += 20) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, height);
      ctx.stroke();
    }
    for (let i = 0; i < height; i += 20) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(width, i);
      ctx.stroke();
    }

    // Desenhar peça
    ctx.fillStyle = '#e8dcc8';
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 2;
    ctx.fillRect(offsetX, offsetY, larguraDesenho, alturaDesenho);
    ctx.strokeRect(offsetX, offsetY, larguraDesenho, alturaDesenho);

    // Desenhar bordas coloridas
    const bordas = configAtual.bordas;
    ctx.lineWidth = 6;

    // TOPO - desenhado em cima (offsetY)
    if (bordas.topo && bordas.topo !== 'nenhum') {
      ctx.strokeStyle = bordas.topo === 'cor' ? '#2ed573' : '#ff7f50';
      ctx.beginPath();
      ctx.moveTo(offsetX, offsetY);
      ctx.lineTo(offsetX + larguraDesenho, offsetY);
      ctx.stroke();
    }

    // BAIXO - desenhado embaixo (offsetY + alturaDesenho)
    if (bordas.baixo && bordas.baixo !== 'nenhum') {
      ctx.strokeStyle = bordas.baixo === 'cor' ? '#2ed573' : '#ff7f50';
      ctx.beginPath();
      ctx.moveTo(offsetX, offsetY + alturaDesenho);
      ctx.lineTo(offsetX + larguraDesenho, offsetY + alturaDesenho);
      ctx.stroke();
    }

    if (bordas.esquerda && bordas.esquerda !== 'nenhum') {
      ctx.strokeStyle = bordas.esquerda === 'cor' ? '#2ed573' : '#ff7f50';
      ctx.beginPath();
      ctx.moveTo(offsetX, offsetY);
      ctx.lineTo(offsetX, offsetY + alturaDesenho);
      ctx.stroke();
    }

    if (bordas.direita && bordas.direita !== 'nenhum') {
      ctx.strokeStyle = bordas.direita === 'cor' ? '#2ed573' : '#ff7f50';
      ctx.beginPath();
      ctx.moveTo(offsetX + larguraDesenho, offsetY);
      ctx.lineTo(offsetX + larguraDesenho, offsetY + alturaDesenho);
      ctx.stroke();
    }

    // Array para armazenar elementos de cota clicáveis
    const novasCotasElementos = [];
    const novasFurosElementos = [];

    // Helpers de cor por face
    const corFuro = (lado) => {
      if (lado === 'LSU') return { stroke: '#2563eb', fill: 'rgba(37,99,235,0.22)', label: 'I', labelColor: '#1e40af' };
      return { stroke: '#d97706', fill: 'rgba(245,158,11,0.22)', label: 'S', labelColor: '#92400e' };
    };

    // Desenhar furos verticais
    ctx.lineWidth = 1;

    furosVerticais.forEach((furo, index) => {
      const xFuro = offsetX + (furo.x * escala);
      const yFuro = offsetY + (furo.y * escala);
      const raio = Math.max(5, (furo.diametro / 2) * escala);
      const cores = corFuro(furo.lado);

      // Círculo do furo
      ctx.strokeStyle = cores.stroke;
      ctx.fillStyle = cores.fill;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(xFuro, yFuro, raio, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // Cruz no centro
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(xFuro - raio * 0.6, yFuro);
      ctx.lineTo(xFuro + raio * 0.6, yFuro);
      ctx.moveTo(xFuro, yFuro - raio * 0.6);
      ctx.lineTo(xFuro, yFuro + raio * 0.6);
      ctx.stroke();

      // Letra S/I dentro do círculo (só se raio for grande o suficiente)
      if (raio >= 7) {
        ctx.font = `bold ${Math.min(raio * 0.85, 11)}px Arial`;
        ctx.fillStyle = cores.labelColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(cores.label, xFuro, yFuro);
      }

      // Calcular mandril
      const mandril = calcularMandril(furo.y, batente);

      // Texto da especificação
      const furoPassante = furo.profundidade === 0 || furo.profundidade >= espessura;
      const sufixoFace = furo.lado === 'LSU' ? '·INF' : '·SUP';
      let textoSpec = furoPassante
        ? `Ø${furo.diametro} M${mandril}${sufixoFace}`
        : `Ø${furo.diametro}x${furo.profundidade} M${mandril}${sufixoFace}`;

      // Offset customizado ou padrão
      const cotaKey = `v_${index}`;
      const offsetCota = configAtual.cotasOffset[cotaKey] || { x: 15, y: -5 };
      
      const textoX = xFuro + offsetCota.x;
      const textoY = yFuro + offsetCota.y;

      const isSelected = cotaSelecionada?.key === cotaKey;
      const accentColor = furo.lado === 'LSU' ? '#2563eb' : '#d97706';

      // Linha de chamada
      ctx.strokeStyle = accentColor;
      ctx.setLineDash([2, 3]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(xFuro + raio, yFuro);
      ctx.lineTo(textoX - 2, textoY + 5);
      ctx.stroke();
      ctx.setLineDash([]);

      // Seta
      ctx.beginPath();
      ctx.moveTo(textoX - 2, textoY + 5);
      ctx.lineTo(textoX - 6, textoY + 2);
      ctx.lineTo(textoX - 6, textoY + 8);
      ctx.closePath();
      ctx.fillStyle = accentColor;
      ctx.fill();

      // Fundo do texto
      ctx.font = 'bold 11px Arial';
      const textWidth = ctx.measureText(textoSpec).width;
      const textHeight = 14;
      const padding = 4;

      ctx.fillStyle = isSelected ? accentColor : 'rgba(255,255,255,0.97)';
      ctx.strokeStyle = accentColor;
      ctx.lineWidth = isSelected ? 2 : 1;
      ctx.beginPath();
      ctx.roundRect(textoX - padding, textoY - textHeight/2 - padding, textWidth + padding*2, textHeight + padding*2, 4);
      ctx.fill();
      ctx.stroke();

      // Texto
      ctx.fillStyle = isSelected ? 'white' : accentColor;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(textoSpec, textoX, textoY + 2);

      // Guardar elemento clicável
      novasCotasElementos.push({
        key: cotaKey,
        tipo: 'vertical',
        furoIndex: index,
        furo: furo,
        texto: textoSpec,
        x: textoX - padding,
        y: textoY - textHeight/2 - padding,
        width: textWidth + padding*2,
        height: textHeight + padding*2,
        offsetX: offsetCota.x,
        offsetY: offsetCota.y,
        furoX: xFuro,
        furoY: yFuro
      });

      novasFurosElementos.push({
        cx: xFuro, cy: yFuro, raio: raio + 4, furo
      });
    });

    // Desenhar furos horizontais (simplificado - mostrar indicador)
    furosHorizontais.forEach((furo, index) => {
      const yFuro = offsetY + (furo.y * escala);
      
      // Indicador de furo horizontal (seta entrando)
      ctx.strokeStyle = '#0066cc';
      ctx.fillStyle = '#0066cc';
      ctx.lineWidth = 2;
      
      const tamanhoSeta = 12;
      let setaX, direcao;

      // Decide só pelo lado (pós-rotação). O fallback furo.x===0 mascarava labels
      // erradas pós-rotação.
      // Regra oficial [CNC] (bancada WoodWOP 6.0.18, 2026-07-24, ver
      // docs/referencia/formato-mpr/campo-bm-horizontal.md §9.1): a broca entra pela borda que
      // dá nome ao BM — XP por X=0 (esquerda), XM por X=comprimento (direita).
      // Estava invertido aqui, desenhando a seta na borda oposta à real, inclusive
      // para peças importadas cujo `lado` já vinha correto do parser STEP.
      // PENDENTE (docs/dividas/registro.md): YP/YM entram pelas bordas SUPERIOR/INFERIOR, não
      // pelas laterais — este bloco ainda os desenha como setas laterais ("simplificado",
      // ver comentário no topo do forEach). Agrupados com XP/XM só para manter o lado
      // da origem coerente (P = origem do eixo) até o layout ser corrigido.
      if (furo.lado === 'XP' || furo.lado === 'YP') {
        setaX = offsetX;
        direcao = 1;
      } else {
        setaX = offsetX + larguraDesenho;
        direcao = -1;
      }

      // Linha da seta
      ctx.beginPath();
      ctx.moveTo(setaX, yFuro);
      ctx.lineTo(setaX + (tamanhoSeta * direcao), yFuro);
      ctx.stroke();

      // Ponta da seta
      ctx.beginPath();
      ctx.moveTo(setaX + (tamanhoSeta * direcao), yFuro);
      ctx.lineTo(setaX + ((tamanhoSeta - 4) * direcao), yFuro - 4);
      ctx.lineTo(setaX + ((tamanhoSeta - 4) * direcao), yFuro + 4);
      ctx.closePath();
      ctx.fill();

      // Calcular mandril
      const mandril = calcularMandril(furo.y, batente);

      // Texto da especificação
      const furoPassante = furo.profundidade === 0 || furo.profundidade >= espessura;
      let textoSpec = furoPassante 
        ? `Ø${furo.diametro} M${mandril}`
        : `Ø${furo.diametro}x${furo.profundidade} M${mandril}`;

      // Offset customizado ou padrão
      const cotaKey = `h_${index}`;
      const defaultOffsetX = direcao === 1 ? -80 : 20;
      const offsetCota = configAtual.cotasOffset[cotaKey] || { x: defaultOffsetX, y: 0 };
      
      const textoX = setaX + offsetCota.x;
      const textoY = yFuro + offsetCota.y;

      // Fundo do texto
      ctx.font = 'bold 11px Arial';
      const textWidth = ctx.measureText(textoSpec).width;
      const textHeight = 14;
      const padding = 4;
      
      const isSelected = cotaSelecionada?.key === cotaKey;
      
      ctx.fillStyle = isSelected ? '#0066cc' : 'rgba(255,255,255,0.95)';
      ctx.strokeStyle = isSelected ? '#0066cc' : '#0066cc';
      ctx.lineWidth = isSelected ? 2 : 1;
      ctx.beginPath();
      ctx.roundRect(textoX - padding, textoY - textHeight/2 - padding, textWidth + padding*2, textHeight + padding*2, 4);
      ctx.fill();
      ctx.stroke();

      // Texto
      ctx.fillStyle = isSelected ? 'white' : '#0066cc';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(textoSpec, textoX, textoY + 2);

      // Guardar elemento clicável
      novasCotasElementos.push({
        key: cotaKey,
        tipo: 'horizontal',
        furoIndex: index,
        furo: furo,
        texto: textoSpec,
        x: textoX - padding,
        y: textoY - textHeight/2 - padding,
        width: textWidth + padding*2,
        height: textHeight + padding*2,
        offsetX: offsetCota.x,
        offsetY: offsetCota.y,
        furoX: setaX,
        furoY: yFuro
      });

      novasFurosElementos.push({
        cx: setaX + (tamanhoSeta / 2) * direcao, cy: yFuro, raio: tamanhoSeta / 2 + 4, furo
      });
    });

    // Desenhar rasgos
    const rasgos = pecaAtual.rasgos || [];
    const novasRasgosElementos = [];
    rasgos.forEach((rasgo) => {
      if (!['superior', 'inferior'].includes(rasgo.face)) return;
      const xCenter = offsetX + parseFloat(rasgo.x) * escala;
      const yCenter = offsetY + parseFloat(rasgo.y) * escala;
      let rw, rh;
      if (rasgo.direcao === 'X') {
        rw = parseFloat(rasgo.comprimento) * escala;
        rh = parseFloat(rasgo.largura) * escala;
      } else {
        rw = parseFloat(rasgo.largura) * escala;
        rh = parseFloat(rasgo.comprimento) * escala;
      }
      const rx = xCenter - rw / 2;
      const ry = yCenter - rh / 2;

      // Fundo branco com borda
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 1.5;
      ctx.fillRect(rx, ry, rw, rh);
      ctx.strokeRect(rx, ry, rw, rh);

      // Hachura diagonal
      ctx.save();
      ctx.beginPath();
      ctx.rect(rx, ry, rw, rh);
      ctx.clip();
      ctx.strokeStyle = 'rgba(80, 80, 80, 0.45)';
      ctx.lineWidth = 0.6;
      const step = 6;
      for (let i = -rh; i < rw + rh; i += step) {
        ctx.beginPath();
        ctx.moveTo(rx + i, ry);
        ctx.lineTo(rx + i + rh, ry + rh);
        ctx.stroke();
      }
      ctx.restore();

      novasRasgosElementos.push({
        x: rx, y: ry, width: rw, height: rh,
        tooltip: `Rasgo: ${rasgo.largura} × ${rasgo.comprimento} × ${rasgo.profundidade} mm`
      });
    });
    rasgosRef.current = novasRasgosElementos;
    furosRef.current = novasFurosElementos;

    // Desenhar cotas de posição do furo em hover
    if (hoveredFuro) {
      const { furo: hf, xCanvas: xH, yCanvas: yH } = hoveredFuro;
      const cotaColor = '#3742fa';
      const arrowSize = 5;

      ctx.save();
      ctx.strokeStyle = cotaColor;
      ctx.fillStyle = cotaColor;
      ctx.lineWidth = 1;
      ctx.font = 'bold 11px Arial';

      // === Cota X — da borda esquerda até o furo (linha horizontal) ===
      const yCotaX = yH + 20;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(offsetX, yH); ctx.lineTo(offsetX, yCotaX + 6);
      ctx.moveTo(xH, yH);      ctx.lineTo(xH, yCotaX + 6);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(offsetX, yCotaX); ctx.lineTo(xH, yCotaX);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(offsetX, yCotaX);
      ctx.lineTo(offsetX + arrowSize, yCotaX - arrowSize / 2);
      ctx.lineTo(offsetX + arrowSize, yCotaX + arrowSize / 2);
      ctx.closePath(); ctx.fill();
      ctx.beginPath();
      ctx.moveTo(xH, yCotaX);
      ctx.lineTo(xH - arrowSize, yCotaX - arrowSize / 2);
      ctx.lineTo(xH - arrowSize, yCotaX + arrowSize / 2);
      ctx.closePath(); ctx.fill();
      const xLabel = `${hf.x} mm`;
      const xLabelW = ctx.measureText(xLabel).width;
      const midX = (offsetX + xH) / 2;
      ctx.fillRect(midX - xLabelW / 2 - 3, yCotaX - 8, xLabelW + 6, 14);
      ctx.fillStyle = 'white';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(xLabel, midX, yCotaX);
      ctx.fillStyle = cotaColor;

      // === Cota Y — do topo da peça até o furo (linha vertical) ===
      const xCotaY = xH - 20;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(xH, offsetY); ctx.lineTo(xCotaY - 6, offsetY);
      ctx.moveTo(xH, yH);      ctx.lineTo(xCotaY - 6, yH);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(xCotaY, offsetY); ctx.lineTo(xCotaY, yH);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(xCotaY, offsetY);
      ctx.lineTo(xCotaY - arrowSize / 2, offsetY + arrowSize);
      ctx.lineTo(xCotaY + arrowSize / 2, offsetY + arrowSize);
      ctx.closePath(); ctx.fill();
      ctx.beginPath();
      ctx.moveTo(xCotaY, yH);
      ctx.lineTo(xCotaY - arrowSize / 2, yH - arrowSize);
      ctx.lineTo(xCotaY + arrowSize / 2, yH - arrowSize);
      ctx.closePath(); ctx.fill();
      const yLabel = `${hf.y} mm`;
      const midY = (offsetY + yH) / 2;
      ctx.save();
      ctx.translate(xCotaY - 14, midY);
      ctx.rotate(-Math.PI / 2);
      const yLabelW = ctx.measureText(yLabel).width;
      ctx.fillStyle = cotaColor;
      ctx.fillRect(-yLabelW / 2 - 3, -8, yLabelW + 6, 14);
      ctx.fillStyle = 'white';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(yLabel, 0, 0);
      ctx.restore();

      ctx.restore();
    }

    // Desenhar cotas principais
    ctx.strokeStyle = '#999';
    ctx.fillStyle = '#333';
    ctx.lineWidth = 1;
    ctx.font = '12px Arial';
    ctx.setLineDash([]);

    // Cota do comprimento (embaixo)
    const yCotaComp = offsetY + alturaDesenho + 30;
    ctx.beginPath();
    ctx.moveTo(offsetX, offsetY + alturaDesenho + 5);
    ctx.lineTo(offsetX, yCotaComp);
    ctx.moveTo(offsetX + larguraDesenho, offsetY + alturaDesenho + 5);
    ctx.lineTo(offsetX + larguraDesenho, yCotaComp);
    ctx.moveTo(offsetX, yCotaComp - 5);
    ctx.lineTo(offsetX + larguraDesenho, yCotaComp - 5);
    ctx.stroke();
    
    // Setas
    ctx.beginPath();
    ctx.moveTo(offsetX, yCotaComp - 5);
    ctx.lineTo(offsetX + 6, yCotaComp - 8);
    ctx.lineTo(offsetX + 6, yCotaComp - 2);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(offsetX + larguraDesenho, yCotaComp - 5);
    ctx.lineTo(offsetX + larguraDesenho - 6, yCotaComp - 8);
    ctx.lineTo(offsetX + larguraDesenho - 6, yCotaComp - 2);
    ctx.closePath();
    ctx.fill();
    
    ctx.textAlign = 'center';
    ctx.fillText(`${comprimento}`, offsetX + larguraDesenho / 2, yCotaComp + 12);

    // Cota da largura (esquerda)
    const xCotaLarg = offsetX - 30;
    ctx.beginPath();
    ctx.moveTo(offsetX - 5, offsetY);
    ctx.lineTo(xCotaLarg, offsetY);
    ctx.moveTo(offsetX - 5, offsetY + alturaDesenho);
    ctx.lineTo(xCotaLarg, offsetY + alturaDesenho);
    ctx.moveTo(xCotaLarg + 5, offsetY);
    ctx.lineTo(xCotaLarg + 5, offsetY + alturaDesenho);
    ctx.stroke();
    
    // Setas
    ctx.beginPath();
    ctx.moveTo(xCotaLarg + 5, offsetY);
    ctx.lineTo(xCotaLarg + 2, offsetY + 6);
    ctx.lineTo(xCotaLarg + 8, offsetY + 6);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(xCotaLarg + 5, offsetY + alturaDesenho);
    ctx.lineTo(xCotaLarg + 2, offsetY + alturaDesenho - 6);
    ctx.lineTo(xCotaLarg + 8, offsetY + alturaDesenho - 6);
    ctx.closePath();
    ctx.fill();
    
    ctx.save();
    ctx.translate(xCotaLarg - 8, offsetY + alturaDesenho / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'center';
    ctx.fillText(`${largura}`, 0, 0);
    ctx.restore();

    // Legenda de faces (canto superior direito)
    const legX = width - 130;
    const legY = 10;
    const legW = 120;
    const legH = 54;
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.strokeStyle = '#ccc';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(legX, legY, legW, legH, 6);
    ctx.fill();
    ctx.stroke();

    ctx.font = 'bold 10px Arial';
    ctx.fillStyle = '#555';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('LEGENDA', legX + 8, legY + 11);

    // Sup
    ctx.fillStyle = 'rgba(245,158,11,0.22)';
    ctx.strokeStyle = '#d97706';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(legX + 18, legY + 28, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.font = 'bold 7px Arial';
    ctx.fillStyle = '#92400e';
    ctx.textAlign = 'center';
    ctx.fillText('S', legX + 18, legY + 28);
    ctx.font = '10px Arial';
    ctx.fillStyle = '#333';
    ctx.textAlign = 'left';
    ctx.fillText('Superior (LS/LSL)', legX + 30, legY + 28);

    // Inf
    ctx.fillStyle = 'rgba(37,99,235,0.22)';
    ctx.strokeStyle = '#2563eb';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(legX + 18, legY + 46, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.font = 'bold 7px Arial';
    ctx.fillStyle = '#1e40af';
    ctx.textAlign = 'center';
    ctx.fillText('I', legX + 18, legY + 46);
    ctx.font = '10px Arial';
    ctx.fillStyle = '#333';
    ctx.textAlign = 'left';
    ctx.fillText('Inferior (LSU)', legX + 30, legY + 46);

    // Info da peça
    ctx.fillStyle = '#333';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(`${pecaAtual.codigo} - ${pecaAtual.nome}`, 10, 10);
    ctx.font = '12px Arial';
    ctx.fillText(`${comprimento} × ${largura} × ${espessura} mm`, 10, 30);
    ctx.fillText(`Batente: ${batente} mm`, 10, 48);

    setCotasElementos(novasCotasElementos);
  }, [pecaAtual, configAtual, cotaSelecionada, pecasOriginais, transformacoes, hoveredFuro]);

  // Desenhar preview quando mudar a peça ou configuração
  useEffect(() => {
    desenharPreview();
  }, [pecaAtualIndex, configPecas, cotaSelecionada, desenharPreview]);

  // Handler de clique no canvas
  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Verificar se clicou em alguma cota
    const cotaClicada = cotasElementos.find(cota => 
      x >= cota.x && x <= cota.x + cota.width &&
      y >= cota.y && y <= cota.y + cota.height
    );

    if (cotaClicada) {
      setCotaSelecionada(cotaClicada);
    } else {
      setCotaSelecionada(null);
    }
  };

  // Handler de mousedown para iniciar arraste
  const handleMouseDown = (e) => {
    if (!cotaSelecionada) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Verificar se está sobre a cota selecionada
    if (x >= cotaSelecionada.x && x <= cotaSelecionada.x + cotaSelecionada.width &&
        y >= cotaSelecionada.y && y <= cotaSelecionada.y + cotaSelecionada.height) {
      setArrastando(true);
    }
  };

  // Handler de mousemove para arrastar e hover
  const handleMouseMove = (e) => {
    if (arrastando && cotaSelecionada) {
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const novoOffsetX = x - cotaSelecionada.furoX;
      const novoOffsetY = y - cotaSelecionada.furoY;

      const comprimento = parseFloat(pecaAtual.comprimento) || 500;
      const largura = parseFloat(pecaAtual.largura) || 300;
      const escala = escalaRef.current;
      const larguraDesenho = comprimento * escala;
      const alturaDesenho = largura * escala;

      const proporcaoX = novoOffsetX / larguraDesenho;
      const proporcaoY = novoOffsetY / alturaDesenho;

      console.log('🎨 Dimensões peça:', { comprimento, largura });
      console.log('🎨 Dimensões desenho:', { larguraDesenho, alturaDesenho });
      console.log('🎨 Offset pixels:', { x: novoOffsetX, y: novoOffsetY });
      console.log('🎨 Proporção:', { x: proporcaoX, y: proporcaoY });

      setConfigPecas(prev => ({
        ...prev,
        [pecaAtual.id]: {
          ...prev[pecaAtual.id],
          cotasOffset: {
            ...prev[pecaAtual.id].cotasOffset,
            [cotaSelecionada.key]: {
              propX: proporcaoX,
              propY: proporcaoY,
              x: novoOffsetX,
              y: novoOffsetY
            }
          }
        }
      }));
      return;
    }

    // Hover — detectar rasgo ou corpo da peça
    const canvas = canvasRef.current;
    if (!canvas || !pecaAtual) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const rasgoHovered = rasgosRef.current.find(r =>
      x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height
    );
    if (rasgoHovered) {
      setTooltip({ x: e.clientX, y: e.clientY, text: rasgoHovered.tooltip });
      return;
    }

    const furoHovered = furosRef.current.find(f => {
      const dx = x - f.cx;
      const dy = y - f.cy;
      return Math.sqrt(dx * dx + dy * dy) <= f.raio;
    });
    if (furoHovered) {
      setHoveredFuro({ furo: furoHovered.furo, xCanvas: furoHovered.cx, yCanvas: furoHovered.cy });
      setTooltip(null);
      return;
    }

    setHoveredFuro(null);
    setTooltip(null);
  };

  // Handler de mouseup para finalizar arraste
  const handleMouseUp = () => {
    setArrastando(false);
  };

  const handleBordaChange = (lado, valor) => {
    setConfigPecas(prev => ({
      ...prev,
      [pecaAtual.id]: {
        ...prev[pecaAtual.id],
        bordas: {
          ...prev[pecaAtual.id].bordas,
          [lado]: valor
        }
      }
    }));
  };

  const handleResetCotas = () => {
    setConfigPecas(prev => ({
      ...prev,
      [pecaAtual.id]: {
        ...prev[pecaAtual.id],
        cotasOffset: {}
      }
    }));
    setCotaSelecionada(null);
  };

  const handleAnterior = () => {
    if (pecaAtualIndex > 0) {
      setPecaAtualIndex(pecaAtualIndex - 1);
      setCotaSelecionada(null);
    }
  };

  const handleProxima = () => {
    if (pecaAtualIndex < pecas.length - 1) {
      setPecaAtualIndex(pecaAtualIndex + 1);
      setCotaSelecionada(null);
    }
  };

  const handleGerar = () => {
    // Incluir transformações no config antes de gerar
    const configComTransformacoes = { ...configPecas };
    Object.keys(transformacoes).forEach(pecaId => {
      if (configComTransformacoes[pecaId]) {
        configComTransformacoes[pecaId].transformacao = transformacoes[pecaId];
      }
    });
    // Persistir bordas junto do fluxo de geração (fire-and-forget).
    // Se a chamada falhar, bordas não persistem — consistência com a semântica atual.
    pecas.forEach(peca => {
      const bordas = configComTransformacoes[peca.id]?.bordas;
      if (bordas) upsertBordas(peca.id, bordas).catch(err => console.error('bordas save:', err));
    });
    onGenerate(configComTransformacoes);
  };

  if (!pecaAtual) {
    return (
      <div className="editor-pdf-overlay">
        <div className="editor-pdf-container">
          <p>Nenhuma peça para editar</p>
          <button onClick={onClose}>Fechar</button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="editor-pdf-overlay">
      <div className="editor-pdf-container">
        {/* Header */}
        <div className="editor-pdf-header">
          <h2>Editor de PDF</h2>
          <div className="editor-pdf-navegacao">
            <button 
              onClick={handleAnterior} 
              disabled={pecaAtualIndex === 0}
              className="btn-nav"
            >
              ← Anterior
            </button>
            <span className="contador-pecas">
              {pecaAtualIndex + 1} / {pecas.length}
            </span>
            <button 
              onClick={handleProxima} 
              disabled={pecaAtualIndex === pecas.length - 1}
              className="btn-nav"
            >
              Próxima →
            </button>
          </div>
          <button onClick={onClose} className="btn-fechar">✕</button>
        </div>

        {/* Conteúdo */}
        <div className="editor-pdf-content">
          {/* Preview */}
          <div className="editor-pdf-preview">
            <canvas 
              ref={canvasRef} 
              width={700} 
              height={500}
              className="canvas-preview"
              onClick={handleCanvasClick}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={() => { handleMouseUp(); setTooltip(null); setHoveredFuro(null); }}
              style={{ cursor: arrastando ? 'grabbing' : (cotaSelecionada ? 'grab' : 'default') }}
            />
          </div>

          {/* Painel de configurações */}
          <div className="editor-pdf-config">
            {/* Cota Selecionada */}
            {cotaSelecionada && (
              <div className="config-section cota-selecionada">
                <h3>Cota Selecionada</h3>
                <div className="cota-info">
                  <p><strong>{cotaSelecionada.texto}</strong></p>
                  <p className="cota-tipo">
                    Furo {cotaSelecionada.tipo === 'vertical' ? 'Vertical' : 'Horizontal'}
                  </p>
                  <p className="cota-hint">Arraste para reposicionar</p>
                </div>
              </div>
            )}
            {/* Transformações */}
            <div className="config-section">
              <h3>Transformações</h3>
              <div className="transform-buttons" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => aplicarTransformacao({
                    ...transformacaoAtual,
                    rotacao: (transformacaoAtual.rotacao + 90) % 360
                  })}
                  className={`btn-transform ${transformacaoAtual.rotacao !== 0 ? 'active' : ''}`}
                  style={{
                    padding: '6px 12px', borderRadius: '6px', cursor: 'pointer',
                    border: transformacaoAtual.rotacao !== 0 ? '2px solid #667eea' : '1px solid #ccc',
                    background: transformacaoAtual.rotacao !== 0 ? '#667eea' : '#fff',
                    color: transformacaoAtual.rotacao !== 0 ? '#fff' : '#333',
                    fontWeight: 'bold', fontSize: '13px'
                  }}
                >
                  ↻ {transformacaoAtual.rotacao}°
                </button>
                <button
                  onClick={() => aplicarTransformacao({
                    ...transformacaoAtual,
                    espelhado: !transformacaoAtual.espelhado
                  })}
                  className={`btn-transform ${transformacaoAtual.espelhado ? 'active' : ''}`}
                  style={{
                    padding: '6px 12px', borderRadius: '6px', cursor: 'pointer',
                    border: transformacaoAtual.espelhado ? '2px solid #667eea' : '1px solid #ccc',
                    background: transformacaoAtual.espelhado ? '#667eea' : '#fff',
                    color: transformacaoAtual.espelhado ? '#fff' : '#333',
                    fontWeight: 'bold', fontSize: '13px'
                  }}
                >
                  ⇄ Espelhar
                </button>
                <button
                  onClick={() => aplicarTransformacao({
                    ...transformacaoAtual,
                    invertido: !transformacaoAtual.invertido
                  })}
                  className={`btn-transform ${transformacaoAtual.invertido ? 'active' : ''}`}
                  style={{
                    padding: '6px 12px', borderRadius: '6px', cursor: 'pointer',
                    border: transformacaoAtual.invertido ? '2px solid #e53e3e' : '1px solid #ccc',
                    background: transformacaoAtual.invertido ? '#e53e3e' : '#fff',
                    color: transformacaoAtual.invertido ? '#fff' : '#333',
                    fontWeight: 'bold', fontSize: '13px'
                  }}
                  title="Inverte as faces de furação: furos superiores viram inferiores e vice-versa"
                >
                  ⬍ Inverter Faces
                </button>
              </div>
            </div>

            {/* Bordas */}
            <div className="config-section">
              <h3>Bordas</h3>
              <div className="bordas-config" key={pecaAtual.id}>
                <div className="borda-item">
                  <label>Topo:</label>
                  <select
                    value={configAtual.bordas.topo || 'nenhum'}
                    onChange={(e) => handleBordaChange('topo', e.target.value)}
                  >
                    <option value="nenhum">Nenhum</option>
                    <option value="cor">Cor</option>
                    <option value="pardo">Pardo</option>
                  </select>
                </div>
                <div className="borda-item">
                  <label>Baixo:</label>
                  <select
                    value={configAtual.bordas.baixo || 'nenhum'}
                    onChange={(e) => handleBordaChange('baixo', e.target.value)}
                  >
                    <option value="nenhum">Nenhum</option>
                    <option value="cor">Cor</option>
                    <option value="pardo">Pardo</option>
                  </select>
                </div>
                <div className="borda-item">
                  <label>Esquerda:</label>
                  <select
                    value={configAtual.bordas.esquerda || 'nenhum'}
                    onChange={(e) => handleBordaChange('esquerda', e.target.value)}
                  >
                    <option value="nenhum">Nenhum</option>
                    <option value="cor">Cor</option>
                    <option value="pardo">Pardo</option>
                  </select>
                </div>
                <div className="borda-item">
                  <label>Direita:</label>
                  <select
                    value={configAtual.bordas.direita || 'nenhum'}
                    onChange={(e) => handleBordaChange('direita', e.target.value)}
                  >
                    <option value="nenhum">Nenhum</option>
                    <option value="cor">Cor</option>
                    <option value="pardo">Pardo</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Info dos furos */}
            <div className="config-section">
<h3>Furos</h3>
              <div className="furos-info">
                {(() => {
                  const vert = pecaAtual.furos?.verticais || [];
                  const horiz = pecaAtual.furos?.horizontais || [];
                  const sup = vert.filter(f => f.lado !== 'LSU').length;
                  const inf = vert.filter(f => f.lado === 'LSU').length;
                  return (
                    <>
                      <div className="furos-face-row">
                        <span className="face-dot face-sup" />
                        <span>Superior</span>
                        <strong>{sup}</strong>
                      </div>
                      <div className="furos-face-row">
                        <span className="face-dot face-inf" />
                        <span>Inferior</span>
                        <strong>{inf}</strong>
                      </div>
                      {horiz.length > 0 && (
                        <div className="furos-face-row">
                          <span style={{ color: '#0066cc', fontSize: 14 }}>H</span>
                          <span>Horizontais</span>
                          <strong>{horiz.length}</strong>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Ações */}
            <div className="config-section">
              <h3>Ações</h3>
              <button onClick={handleResetCotas} className="btn-reset">
                Resetar posição das cotas
              </button>
            </div>

            {/* Dica */}
            <div className="config-section dica">
              <p><strong>Dicas:</strong></p>
              <ul>
                <li>Clique em uma cota para selecionar</li>
                <li>Arraste para reposicionar</li>
                <li>Bordas: <span className="cor-preview cor">●</span> Cor / <span className="cor-preview pardo">●</span> Pardo</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="editor-pdf-footer">
          <button onClick={onClose} className="btn-cancelar">
            Cancelar
          </button>
          <button onClick={handleGerar} className="btn-gerar">
            Gerar {pecas.length} PDF(s)
          </button>
        </div>
      </div>
    </div>

    {tooltip && (
      <div className="canvas-tooltip" style={{ left: tooltip.x + 14, top: tooltip.y - 38 }}>
        {tooltip.text}
      </div>
    )}
    </>
  );
}

export default EditorPDF;